import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import { AUTH_QUERY_KEY } from '@/auth/AuthProvider';
import type { MfaManageRequest, MfaManageResponse, MfaStatusResponse } from '@/api/types';

/**
 * Data access for the signed-in user's own multi-factor enrolment.
 *
 * Only the enrolment side lives here. The login challenge is deliberately absent:
 * its caller is not signed in yet — they hold Identity's two-factor cookie and
 * nothing else — so there is no session for react-query to cache against, and
 * caching a one-shot challenge would be actively wrong. `MfaChallengePage` calls
 * those endpoints directly.
 *
 * The whole enrolment lifecycle is one endpoint, `POST /api/mfa/2fa`, taking
 * ASP.NET Identity's own `TwoFactorRequest`. The three hooks below are named for
 * what they mean rather than for the flag they set, so callers do not have to
 * remember which combination does what.
 */

const BASE = '/api/mfa';

export const mfaKeys = {
  all: ['mfa'] as const,
  status: ['mfa', 'status'] as const,
};

/**
 * Every MFA mutation changes something `/api/auth/me` reports, because enrolling
 * clears the `mustSetupMfa` claim and the server re-issues the cookie. Refreshing
 * only the MFA query would leave the route guard holding a stale flag and bounce
 * the user back to the setup screen they just completed.
 */
function useInvalidateMfa() {
  const queryClient = useQueryClient();

  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: mfaKeys.all }),
      queryClient.invalidateQueries({ queryKey: AUTH_QUERY_KEY }),
    ]);
  };
}

function manage(body: MfaManageRequest) {
  return api.post<MfaManageResponse>(`${BASE}/2fa`, body);
}

export function useMfaStatus() {
  return useQuery<MfaStatusResponse>({
    queryKey: mfaKeys.status,
    queryFn: () => api.get<MfaStatusResponse>(BASE),
  });
}

/**
 * Mints a new authenticator secret and returns it as a QR code.
 *
 * This turns MFA off until a code confirms the new secret — Identity's rule, and
 * the reason it cannot be combined with enabling in one call. `currentPassword`
 * is required only when replacing an authenticator that already works, because
 * the old secret is destroyed the moment this succeeds.
 *
 * Invalidates on success: for an already-enrolled user this call clears
 * `isTwoFactorEnabled`, which the Profile card and the route guard both read.
 */
export function useResetAuthenticatorKey() {
  const invalidate = useInvalidateMfa();

  return useMutation({
    mutationFn: (currentPassword?: string) =>
      manage({ twoFactor: { resetSharedKey: true }, currentPassword: currentPassword ?? null }),
    onSuccess: invalidate,
  });
}

/** Confirms a code against the pending secret and turns MFA on. */
export function useEnableMfa() {
  const invalidate = useInvalidateMfa();

  return useMutation({
    mutationFn: (code: string) => manage({ twoFactor: { enable: true, twoFactorCode: code } }),
    onSuccess: invalidate,
  });
}

/**
 * Issues a fresh set of recovery codes, invalidating every code handed out
 * before. The new codes come back on the response and are shown once — the
 * server keeps only their hashes, so there is no second chance to read them.
 */
export function useRegenerateRecoveryCodes() {
  const invalidate = useInvalidateMfa();

  return useMutation({
    mutationFn: (currentPassword: string) =>
      manage({ twoFactor: { resetRecoveryCodes: true }, currentPassword }),
    onSuccess: invalidate,
  });
}

/** Turns MFA off. Refused with 403 when the user's role compels it. */
export function useDisableMfa() {
  const invalidate = useInvalidateMfa();

  return useMutation({
    mutationFn: (currentPassword: string) =>
      manage({ twoFactor: { enable: false }, currentPassword }),
    onSuccess: invalidate,
  });
}
